import { Injectable, OnDestroy } from '@angular/core';
import { versions } from '../../../../environments/versions';

import { ReplaySubject, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../modules/auth/services/auth.service';

import { NavigationEnd, Router } from '@angular/router';
import { LittlesisLicensingService } from '../../../modules/licensing/services/littlesis-licensing.service';
import { UserEncodingService } from '../analytics/user-encoding.service';
import { LspEvents } from './analytics-events.constant';

declare global {
  interface Window {
    gtag: any;
  }
}

window.gtag = window.gtag || {};

export interface AnalyticsEventProperties {
  [key: string]: string | number | boolean;
  category: string;
  label: string;
  value?: string;
}

export interface AnalyticsEvent {
  action: string;
  properties: AnalyticsEventProperties;
}



interface UserProperties {
  uuid: string;
  customerId: string;
  domain: string;
  encodedUser?: string;
  licensed?: boolean;
  bundle?: string;
  tier?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  private userProperties: ReplaySubject<UserProperties>;
  private currentScreen: string;
  private onDestroy$: Subject<void>;

  constructor(
    private authService: AuthService,
    private userEncodingService: UserEncodingService,
    private router: Router,
    private _licenseSvc: LittlesisLicensingService,
  ) {
    this.userProperties = new ReplaySubject<UserProperties>();
    this.onDestroy$ = new Subject();
    this.router.events.pipe(filter((ev) => ev instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
      let path: string;
      if (ev?.url) {
        path = ev.url.indexOf('?') < 0 ? ev.url.slice(1, ev.url.length) : ev.url.slice(1, ev.url.indexOf('?'));
      } else {
        path = ev.url;
      }
      this.currentScreen = path;
      window.gtag('event', 'current_view', { view_url: this.currentScreen });
    });
    combineLatest([
      this.authService.appUser$
      .pipe(
        filter((user) => !!user?.gId),
        switchMap((user) =>
          this.userEncodingService.encodeUser(user.email).pipe(
            map((encodedEmail) => {
              user.encodedUser = encodedEmail;
              return user;
            }),
          ),
        )
      ),
      this._licenseSvc.checkLicense(false)
      .pipe(
        filter(licenseState => licenseState.loaded)
      )
    ]).pipe(
      take(1),
      takeUntil(this.onDestroy$)
    ).subscribe(([user,licenseState])=>{

      const userProps = {
        uuid: user.gId,
        customerId: user.customer.customerId,
        domain: user.email.split('@')[1],
        encodedUser: user.encodedUser,
        licensed: licenseState.licensed,
        bundle: licenseState.bundle,
        tier: licenseState.tier,
      };

      window.gtag('set', 'user_id', user.encodedUser);

      window.gtag('set', 'user_properties', {
        uuid: userProps.uuid,
        customerId: userProps.customerId,
        domain: userProps.domain,
        licensed: licenseState.licensed,
        bundle: licenseState.bundle,
        tier: licenseState.tier,
        app_name: 'Little Sis Sync',
        app_version: versions.version,
      });
      this.sendEvent(LspEvents.lspEvents.home.login as AnalyticsEvent);
      this.userProperties.next(userProps);
    });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next(null);
  }

  sendEvent(event: AnalyticsEvent): void {
    this.userProperties.pipe(filter((userProps: UserProperties) => !!userProps?.encodedUser))
    .subscribe((userProps: UserProperties) => {
      window.gtag('event', event.action, {
        ...userProps,
        ...event.properties,
        view_url: this.currentScreen,
      });
    });
  }
}
